import React, { FormEvent, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';

import AuthCard from '@/features/auth/components/auth-card/AuthCard';
import GoogleButton from '@/features/auth/components/google-button/GoogleButton';
import { carryRedirect, safeRedirect } from '@/features/auth/safeRedirect';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Register(props: RouteComponentProps) {
  // Carried from /login, so someone who signs up from a `tile login` link
  // lands back on the approval page rather than on /apps.
  const redirectTo = safeRedirect(props.location.search);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) return setError('Name is required.');
    if (!EMAIL_RE.test(email)) return setError('Enter a valid email address.');
    if (password.length < 8) return setError('Password must be at least 8 characters.');

    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ name: name.trim(), email, password }),
      });
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        setError((data.error as string) || 'Registration failed.');
        return;
      }
      window.location.assign(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title="Create your account"
      subtitle="Build production apps without leaving the browser."
    >
      {error ? <div className="auth-banner auth-banner-error">{error}</div> : null}
      {/* Renders nothing at all unless tile-auth reports Google as configured. */}
      <GoogleButton redirectTo={redirectTo} onError={setError} text="signup_with" />
      <form onSubmit={onSubmit} noValidate>
        <label className="auth-label" htmlFor="name">
          Name
        </label>
        <input
          id="name"
          type="text"
          autoComplete="name"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="auth-input"
          required
        />
        <label className="auth-label" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="auth-input"
          required
        />
        <label className="auth-label" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          placeholder="At least 8 characters"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="auth-input"
          required
        />
        <button type="submit" disabled={submitting} className="auth-btn auth-btn-primary">
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="auth-foot">
        Already have an account?{' '}
        <Link to={`/login${carryRedirect(props.location.search)}`} className="auth-link">
          Sign in
        </Link>
      </p>
    </AuthCard>
  );
}
